import type {
  Template,
  MeasurementGroup,
  MeasurementLabel,
} from '@/models/template';
import type { Measurement } from '@/models/report';
import type { AIMeasurement } from '@/models/aiScan';
import { formatNormalRange } from '@/shared/utils/formatNormalRange';
import type { ReferenceRanges } from '@/shared/utils/referenceRangeRules';
import { preferAIVariant } from './preferAIVariant';
import type { MeasurementGroupDisplay } from '../types/reportDetail';

/**
 * Combines template measurement groups with actual report values
 * to produce display-ready data for MeasurementCards.
 *
 * `patientSex` and `patientAge` (whole years at the study date) pick the
 * patient's severity bands from each measurement's reference_ranges when
 * deriving the normal-range string.
 *
 * `aiMeasurements` mirrors the exported PDF (`exports/mixins.py` on the
 * backend): a saved value always wins, an AI value fills a row that has none,
 * and a row with neither stays blank. AI-sourced rows are flagged `isAIValue`
 * so the card can mark them. Callers pass an empty array to switch the fallback
 * off — a finalized report renders only what was saved when it was signed.
 *
 * Note this is deliberately NOT the worksheet's rule (MeasurementsPanel), which
 * has five dot states and prefers the AI value when SR and AI agree. This view
 * sits beside the PDF preview and matches the printed document instead.
 */
export function buildMeasurementGroups(
  template: Template | undefined,
  reportMeasurements: Measurement[],
  patientSex: string | null | undefined = null,
  aiMeasurements: AIMeasurement[] = [],
  patientAge: number | null | undefined = null
): MeasurementGroupDisplay[] {
  if (!template?.measurementGroups?.length) return [];

  const valueByClass = new Map<string, number | null>();
  reportMeasurements.forEach((m) => {
    valueByClass.set(
      m.canonicalMeasurement,
      typeof m.value === 'number' ? m.value : null
    );
  });

  // Several AI variants can share one canonical (LV EF from biplane, A4C and
  // A2C), and they are re-created on every refetch, so rank them rather than
  // letting payload order decide. Dismissed rows never surface.
  const aiByClass = new Map<string, AIMeasurement>();
  aiMeasurements.forEach((ai) => {
    if (!ai.canonicalMeasurement || ai.isDeleted) return;
    if (typeof ai.value !== 'number') return;
    aiByClass.set(
      ai.canonicalMeasurement,
      preferAIVariant(aiByClass.get(ai.canonicalMeasurement), ai)
    );
  });

  return [...template.measurementGroups]
    .sort(
      (a: MeasurementGroup, b: MeasurementGroup) =>
        (a.index ?? 0) - (b.index ?? 0)
    )
    .map((group: MeasurementGroup) => {
      const items = [...(group.measurementLabels ?? [])]
        .sort(
          (a: MeasurementLabel, b: MeasurementLabel) =>
            (a.index ?? 0) - (b.index ?? 0)
        )
        .map((ml: MeasurementLabel) => {
          // A row created then cleared is stored as null, so test the value
          // rather than the key's presence — it must fall through to AI.
          const savedValue = ml.canonicalMeasurement
            ? (valueByClass.get(ml.canonicalMeasurement) ?? null)
            : null;
          const ai =
            savedValue === null && ml.canonicalMeasurement
              ? aiByClass.get(ml.canonicalMeasurement)
              : undefined;

          let value = '—';
          let unit = ml.unit ?? null;
          let isAIValue = false;
          if (savedValue !== null) {
            value = Number(savedValue).toFixed(ml.precision ?? 0);
          } else if (ai) {
            // Precision always comes from the template; AI carries none.
            value = Number(ai.value).toFixed(ml.precision ?? 0);
            // `AIMeasurement.unit` is non-nullable, so an empty string is the
            // "no unit" case — fall back to the template's.
            unit = ai.unit || ml.unit || null;
            isAIValue = true;
          }

          return {
            key: ml.uuid ?? ml.canonicalMeasurement ?? ml.label,
            label: ml.label,
            value,
            unit,
            normalRange: formatNormalRange(
              ml.referenceRanges as ReferenceRanges | null | undefined,
              patientSex,
              patientAge
            ),
            isAIValue,
          };
        });

      return { groupLabel: group.label, items };
    });
}
